import {useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {isOk} from '../../../utils/apiResponse';
import {AlertRulesApi} from '../../../api/alertRulesApi';
import {
    availableChannels,
    CHANNEL_EMAIL,
    draftFromRule,
    ERROR_NOT_FOUND,
    ERROR_WEBHOOK_NOT_CONFIGURED,
    errorFromResponse,
    hasChannels,
    insertAlertIn,
    isSystemRule,
    limitReached,
    removeRuleFrom,
    replaceRuleIn,
    ruleFromResponse,
    ruleUpdateFromDraft,
    withChannels,
    withViewerOptOut,
} from '../rulesModel';
import {showRuleUndoToast} from './ruleUndoToast';

/**
 * Las escrituras de una fila del Centro convertido, compartidas por «Mis reglas» y «Avisos de las cuentas» (UD-18):
 * el switch Activa / Pausada, «Dejar de recibirla», «Volver al número de la agencia», «Eliminar», el canal webhook y
 * a quién le llega.
 *
 * Cada una pinta antes, se corrige con la respuesta y vuelve atrás si falla. Una fila escribe de a una cosa por vez
 * (`isBusy`): así la vuelta atrás con la foto del clic y la respuesta entera no pisan otra escritura (review
 * C-av2-fe-centro-07). La respuesta y la vuelta atrás se aplican aunque la lista ya no esté a la vista: el payload vive
 * en la pantalla (C-av2-fe-centro-06). Con el filtro de una persona (`person`), lo que vuelve de una escritura viene
 * visto como el admin: la lista se pide de nuevo (`reload`).
 */
export function useRuleWrites({onPayloadChange, reload = null, person = null, limits = null, onRestored = null}) {
    const {t} = useTranslation();
    const [busy, setBusy] = useState({});
    const mounted = useRef(true);

    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    const setRuleBusy = useCallback((id, value) => setBusy((current) => ({...current, [String(id)]: value})), []);
    const isBusy = (id) => Boolean(busy[String(id)]);
    const apply = (rule) => onPayloadChange((current) => replaceRuleIn(current, rule));
    const reloadForPerson = () => {
        if (person && reload) reload();
    };

    // La regla ya no existe (otra pestaña la borró, o ya no se ve): sale de la lista con su aviso.
    const forget = (rule) => {
        onPayloadChange((current) => removeRuleFrom(current, rule.id));
        toast.error(t('alerts_center.rule_not_found'));
    };

    const maxActive = limits && typeof limits.max_active === 'number' ? limits.max_active : (limits && limits.max) || '';

    const write = (rule, optimistic, request, {fallbackKey, onSuccess = null, onError = null} = {}) => {
        if (isBusy(rule.id)) return;
        setRuleBusy(rule.id, true);
        apply(optimistic);
        request().then((response) => {
            if (mounted.current) setRuleBusy(rule.id, false);
            if (!isOk(response)) {
                const error = errorFromResponse(response, {fallbackKey});
                if (error.code === ERROR_NOT_FOUND) {
                    forget(rule);
                    return;
                }
                apply(rule);
                if (onError && onError(error)) return;
                toast.error(t(error.key, {max: maxActive}));
                return;
            }
            const saved = ruleFromResponse(response);
            if (saved) apply(saved);
            if (onSuccess) onSuccess(saved);
            reloadForPerson();
        });
    };

    /**
     * El switch Activa / Pausada (ronda 5, D18): `is_active`. Prender una propia respeta el tope (cuenta sólo las
     * propias), y prender una que había quedado sin canales le pone el correo, que va siempre: activa y sin canales no
     * le avisaría a nadie. Un aviso que no sale por correo (el saldo de un backend anterior) va por el canal que tiene.
     */
    const toggleActive = (rule, next) => {
        if (next && !isSystemRule(rule) && limitReached(limits)) {
            toast.error(t('alerts_center.builder.limit_reached', {max: maxActive}));
            return;
        }
        const patch = {is_active: next};
        let optimistic = {...rule, is_active: next, paused_reason: next ? null : rule.paused_reason};
        if (next && !hasChannels(rule)) {
            const available = availableChannels(rule);
            const channels = available.includes(CHANNEL_EMAIL) ? [CHANNEL_EMAIL] : available.slice(0, 1);
            const recipients = (rule.delivery && rule.delivery.recipients) || {};
            // Sin `excluded_user_ids`: sin la clave, el backend deja las bajas como están (R, ronda 5).
            patch.delivery = {channels, recipients: {type: recipients.type, user_ids: recipients.user_ids || []}};
            optimistic = withChannels(optimistic, channels);
        }
        write(rule, optimistic, () => AlertRulesApi.update(rule.id, patch), {
            fallbackKey: 'alerts_center.rules.toggle_error',
            onSuccess: () => toast.success(t(next ? 'alerts_center.row.activated_toast' : 'alerts_center.row.paused_toast')),
        });
    };

    // «Dejar de recibirla» / «Volver a recibirla» (D19): la baja de quien mira, en una regla de equipo.
    const optOut = (rule, optedOut) => write(rule, withViewerOptOut(rule, optedOut),
        () => (optedOut ? AlertRulesApi.optOut(rule.id) : AlertRulesApi.undoOptOut(rule.id)),
        {fallbackKey: 'alerts_center.row.opt_out_error'});

    // «Deshacer» de un borrado: la regla vuelve a su lugar en la lista.
    const restore = (rule) => {
        AlertRulesApi.restore(rule.id).then((response) => {
            if (!isOk(response)) {
                toast.error(t('alerts_center.rules.restore_error'));
                return;
            }
            const restored = ruleFromResponse(response) || rule;
            onPayloadChange((current) => insertAlertIn(current, restored));
            if (mounted.current && onRestored) onRestored(restored);
            reloadForPerson();
        });
    };

    // Borra la fila con «Deshacer» unos segundos (el borrado es soft). `messageKey` dice qué pasó.
    const removeWithUndo = (rule, messageKey) => {
        if (isBusy(rule.id)) return;
        setRuleBusy(rule.id, true);
        AlertRulesApi.remove(rule.id).then((response) => {
            if (mounted.current) setRuleBusy(rule.id, false);
            if (!isOk(response)) {
                const error = errorFromResponse(response, {fallbackKey: 'alerts_center.rules.delete_error'});
                if (error.code === ERROR_NOT_FOUND) forget(rule);
                else toast.error(t(error.key));
                return;
            }
            onPayloadChange((current) => removeRuleFrom(current, rule.id));
            showRuleUndoToast({
                message: t(messageKey),
                undoLabel: t('alerts_center.rules.undo'),
                onUndo: () => restore(rule),
                actionId: rule.id,
            });
            reloadForPerson();
        });
    };

    // «Volver al número de la agencia» (una excepción): sus líneas vuelven a la alerta de toda la agencia.
    const returnToAgency = (rule) => removeWithUndo(rule, 'alerts_center.row.returned_toast');

    // «Eliminar» una propia.
    const remove = (rule) => removeWithUndo(rule, 'alerts_center.rules.deleted_toast');

    /**
     * El chip «Webhook» (UD-18): suma o saca el webhook, con el correo que va siempre. Sin la dirección conectada, el
     * backend contesta `WEBHOOK_NOT_CONFIGURED` y la pantalla abre el panel para conectarlo (`onNeedsWebhook`).
     */
    const setChannels = (rule, channels, {onNeedsWebhook = null} = {}) => write(rule, withChannels(rule, channels),
        () => AlertRulesApi.setChannels(rule.id, channels), {
            fallbackKey: 'alerts_center.row.channels_error',
            onError: (error) => {
                if (error.code !== ERROR_WEBHOOK_NOT_CONFIGURED || !onNeedsWebhook) return false;
                onNeedsWebhook(rule);
                return true;
            },
        });

    /**
     * A quién le llega (el selector de «Avisos de las cuentas», UD-18): el mismo PUT del «Para» del constructor, con
     * sólo el bloque `delivery`. `recipients` es el del borrador: `{type, userIds, excludedUserIds, excludedKnown}`.
     */
    const setRecipients = (rule, recipients) => {
        const before = draftFromRule(rule);
        const update = ruleUpdateFromDraft(rule, {...before, recipients});
        if (!update.delivery) return;
        const current = (rule.delivery && rule.delivery.recipients) || {};
        const sent = update.delivery.recipients;
        const next = {...current, type: recipients.type, user_ids: sent.user_ids};
        // Las bajas nuevas todavía no tienen nombre del backend: hasta la respuesta, el «salvo …» los saca del equipo.
        if (sent.excluded_user_ids) {
            next.excluded_user_ids = sent.excluded_user_ids;
            delete next.excluded;
        }
        // Las personas nuevas, igual: sin `resolved` el «Para» dice cuántas son hasta que vuelve la respuesta.
        if (recipients.type !== current.type || String(sent.user_ids) !== String(current.user_ids || [])) delete next.resolved;
        const optimistic = {...rule, delivery: {...rule.delivery, recipients: next}};
        write(rule, optimistic, () => AlertRulesApi.update(rule.id, {delivery: update.delivery}), {
            fallbackKey: 'alerts_center.row.recipients_error',
            onSuccess: () => toast.success(t('alerts_center.accounts.recipients_saved')),
        });
    };

    return {isBusy, toggleActive, optOut, returnToAgency, remove, restore, setChannels, setRecipients};
}

export default useRuleWrites;
